import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Building2, Save, X as XIcon, Upload } from "lucide-react";
import Navbar from "../components/Navbar";
import { SkillLibraryCard } from "../components/SkillLibraryCard";
import { api } from "../lib/api";
import { useAuth, isSuperAdmin } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
/** Resolve `/api/...` paths against the backend URL so <img> tags work across environments. */
function absLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function OrganizationsPage() {
  const { user, refresh: refreshMe } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", accent_color: "#d8b854", logo_url: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, accent_color, logo_url }
  const fileInputRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.listOrgs()
      .then(setOrgs)
      .catch(() => toast.error("Couldn't load organizations"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  if (!isSuperAdmin(user)) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-10 py-12 text-center">
          <div className="text-lg text-white">Super-admin only</div>
          <div className="mt-2 text-xs uppercase tracking-widest text-[#757575]">
            You don't have access to organization management.
          </div>
        </main>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.createOrg({
        name: form.name.trim(),
        accent_color: form.accent_color,
        logo_url: form.logo_url.trim() || null,
      });
      toast.success("Organization created");
      setForm({ name: "", accent_color: "#d8b854", logo_url: "" });
      refresh();
      refreshMe();  // super-admin sees every org, so their org list also updates
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const remove = async (org) => {
    if (!window.confirm(`Delete "${org.name}" and ALL its data (players, workouts, snapshots)? This cannot be undone.`)) return;
    try {
      await api.deleteOrg(org.id);
      toast.success("Organization deleted");
      refresh(); refreshMe();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const startEdit = (o) => setEditing({ ...o });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    try {
      await api.updateOrg(editing.id, {
        name: editing.name.trim(),
        accent_color: editing.accent_color,
        logo_url: (editing.logo_url || "").trim() || null,
      });
      toast.success("Organization updated");
      setEditing(null);
      refresh(); refreshMe();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const uploadLogo = async (orgId, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setUploadingId(orgId);
    try {
      await api.uploadOrgLogo(orgId, file);
      toast.success("Logo uploaded");
      refresh(); refreshMe();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingId(null); }
  };

  const clearLogo = async (orgId) => {
    if (!window.confirm("Remove this org's logo?")) return;
    try {
      await api.deleteOrgLogo(orgId);
      toast.success("Logo removed");
      refresh(); refreshMe();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8" data-testid="organizations-page">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl text-white uppercase tracking-tight leading-none">Organizations</h1>
          <div className="mt-1 h-[2px] w-16 bg-[var(--org-accent)]" />
          <div className="mt-3 text-xs uppercase tracking-widest text-[#a0a0a0]">
            Each org has its own isolated pool of players, workouts, and inventory data.
          </div>
        </div>

        {/* Create form */}
        <form onSubmit={create} className="border border-white/10 bg-[#1c1c1c] p-5 space-y-4" data-testid="create-org-form">
          <div className="flex items-center gap-2 mb-1">
            <Plus className="w-4 h-4 text-[var(--org-accent)]" />
            <div className="font-heading text-lg text-white uppercase">New Organization</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_140px_2fr_auto] gap-3 items-end">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] mb-1.5">Name</div>
              <input placeholder="e.g., Elite Sports Agency" value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full h-10 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
                required data-testid="new-org-name" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] mb-1.5">Accent</div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.accent_color}
                  onChange={(e) => setForm(f => ({ ...f, accent_color: e.target.value }))}
                  className="h-10 w-10 p-0 border border-white/10 bg-[#101010] cursor-pointer"
                  data-testid="new-org-accent" />
                <input type="text" value={form.accent_color}
                  onChange={(e) => setForm(f => ({ ...f, accent_color: e.target.value }))}
                  className="flex-1 h-10 px-2 bg-[#101010] border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-[var(--org-accent)]" />
              </div>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] mb-1.5">Logo URL (optional)</div>
              <input placeholder="https://…/logo.png" value={form.logo_url}
                onChange={(e) => setForm(f => ({ ...f, logo_url: e.target.value }))}
                className="w-full h-10 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
                data-testid="new-org-logo" />
            </label>
            <button type="submit" disabled={busy}
              className="h-10 px-5 flex items-center gap-2 bg-[var(--org-accent)] text-[#121212] text-xs uppercase tracking-[0.25em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              style={{ transition: "background-color 0.15s ease" }}
              data-testid="create-org-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
          </div>
        </form>

        {/* Orgs list */}
        <div className="border border-white/10 bg-[#1c1c1c]" data-testid="orgs-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#a0a0a0]">
                <th className="px-4 py-3 border-b border-white/10">Name</th>
                <th className="px-4 py-3 border-b border-white/10">Accent</th>
                <th className="px-4 py-3 border-b border-white/10">Logo</th>
                <th className="px-4 py-3 border-b border-white/10">Slug</th>
                <th className="px-4 py-3 border-b border-white/10">Created</th>
                <th className="px-4 py-3 border-b border-white/10 w-24 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[var(--org-accent)]" /></td></tr>
              )}
              {!loading && orgs.map(o => {
                const isEditing = editing && editing.id === o.id;
                return (
                  <tr key={o.id} className="hover:bg-white/[0.02] align-top" data-testid={`org-row-${o.id}`}>
                    <td className="px-4 py-3 border-b border-white/5 text-white">
                      {isEditing ? (
                        <input value={editing.name} onChange={(e) => setEditing(v => ({ ...v, name: e.target.value }))}
                          className="w-full h-8 px-2 bg-[#101010] border border-white/10 text-sm focus:outline-none focus:border-[var(--org-accent)]" />
                      ) : (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-[var(--org-accent)]" />
                          <span>{o.name}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-white/5">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input type="color" value={editing.accent_color}
                            onChange={(e) => setEditing(v => ({ ...v, accent_color: e.target.value }))}
                            className="h-8 w-8 p-0 border border-white/10 bg-[#101010] cursor-pointer" />
                          <input type="text" value={editing.accent_color}
                            onChange={(e) => setEditing(v => ({ ...v, accent_color: e.target.value }))}
                            className="w-24 h-8 px-2 bg-[#101010] border border-white/10 text-xs font-mono text-white focus:outline-none focus:border-[var(--org-accent)]" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-4 h-4 border border-white/20" style={{ background: o.accent_color }} />
                          <span className="font-mono text-xs text-[#a0a0a0]">{o.accent_color}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-white/5">
                      {isEditing ? (
                        <div className="space-y-1.5">
                          <input value={editing.logo_url || ""} placeholder="/api/orgs/.../logo or https://…"
                            onChange={(e) => setEditing(v => ({ ...v, logo_url: e.target.value }))}
                            className="w-full h-8 px-2 bg-[#101010] border border-white/10 text-xs text-white focus:outline-none focus:border-[var(--org-accent)]" />
                          <div className="text-[9px] text-[#757575] uppercase tracking-widest">or upload:</div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {o.logo_url
                            ? <img src={absLogoUrl(o.logo_url)} alt={o.name} className="h-6 w-auto object-contain"
                                onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            : <span className="text-[#575757] text-xs italic">—</span>}
                          <label className="cursor-pointer inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-[var(--org-accent)]"
                            data-testid={`upload-logo-${o.id}`}>
                            {uploadingId === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            Upload
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                              className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(o.id, f); e.target.value = ""; }}
                            />
                          </label>
                          {o.logo_url && (
                            <button onClick={() => clearLogo(o.id)}
                              className="text-[10px] uppercase tracking-widest text-[#757575] hover:text-red-400"
                              data-testid={`clear-logo-${o.id}`}>
                              Clear
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-white/5 text-[#a0a0a0] font-mono text-xs">{o.slug}</td>
                    <td className="px-4 py-3 border-b border-white/5 text-[#a0a0a0] font-mono text-xs">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-3 border-b border-white/5 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit}
                            className="mr-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[var(--org-accent)] hover:text-[var(--org-accent-hover)]"
                            data-testid={`save-org-${o.id}`}>
                            <Save className="w-3.5 h-3.5" /> Save
                          </button>
                          <button onClick={cancelEdit}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#757575] hover:text-white"
                            data-testid={`cancel-edit-org-${o.id}`}>
                            <XIcon className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(o)}
                            className="mr-2 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-[var(--org-accent)]"
                            data-testid={`edit-org-${o.id}`}>
                            Edit
                          </button>
                          <button onClick={() => remove(o)} className="text-[#757575] hover:text-red-400"
                            style={{ transition: "color 0.15s ease" }}
                            data-testid={`delete-org-${o.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && orgs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#757575]">No organizations yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <SkillLibraryCard />
      </main>
    </div>
  );
}
